import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useSettings } from "../hooks/useSettings";
import { useSettingsApi } from "../api/settingsApi";
import { useAuth } from "../context/AuthContext";
import ScheduleWindow from "../components/settings/ScheduleWindow";

// O backend guarda "1,2,3"; a tela trabalha com números.
const toWeekdays = (value) =>
  String(value ?? "1,2,3,4,5")
    .split(",")
    .map(Number)
    .filter((day) => day >= 1 && day <= 7);

const Settings = () => {
  const { user } = useAuth();
  const api = useSettingsApi();
  const isAdmin = user?.role === "ADMIN";
  const {
    settings,
    loading,
    error,
    fetchSettings,
    updateSettings,
  } = useSettings();

  const [formData, setFormData] = useState({
    summarizeUrl: "",
    summarizeToken: "",
    analysisEnabled: false,
    analysisInactivityMinutes: 30,
    analysisMinMessages: 4,
    signMessagesWithAgent: true,
    responseSlaMinutes: 30,
    slaBusinessHoursEnabled: false,
    slaStartMinute: 480,
    slaEndMinute: 1080,
    slaWeekdays: [1, 2, 3, 4, 5],
    analysisWindowEnabled: false,
    analysisStartMinute: 480,
    analysisEndMinute: 1080,
    analysisWeekdays: [1, 2, 3, 4, 5],
    pm2ProcessName: "",
    alertEmailEnabled: false,
    alertEmailTo: "",
    smtpHost: "smtp.hostinger.com",
    smtpPort: 465,
    smtpSecure: true,
    smtpUser: "",
    // Nunca vem preenchida do servidor: em branco significa "manter a atual".
    smtpPassword: "",
    smtpFrom: "",
    googleContactsEnabled: false,
    googleClientId: "",
    googleClientSecret: "",
    googleContactGroup: "Leads WhatsApp",
    callTranscriptionEnabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [pm2List, setPm2List] = useState([]);
  const [restarting, setRestarting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [syncingGoogle, setSyncingGoogle] = useState(false);

  const resetForm = useCallback(() => {
    setFormData({
      summarizeUrl: settings?.summarizeUrl || "",
      summarizeToken: settings?.summarizeToken || "",
      analysisEnabled: settings?.analysisEnabled || false,
      analysisInactivityMinutes: settings?.analysisInactivityMinutes || 30,
      analysisMinMessages: settings?.analysisMinMessages || 4,
      signMessagesWithAgent: settings?.signMessagesWithAgent !== false,
      responseSlaMinutes: settings?.responseSlaMinutes ?? 30,
      slaBusinessHoursEnabled: settings?.slaBusinessHoursEnabled === true,
      slaStartMinute: settings?.slaStartMinute ?? 480,
      slaEndMinute: settings?.slaEndMinute ?? 1080,
      // O backend guarda "1,2,3"; a tela trabalha com números.
      slaWeekdays: toWeekdays(settings?.slaWeekdays),
      analysisWindowEnabled: settings?.analysisWindowEnabled === true,
      analysisStartMinute: settings?.analysisStartMinute ?? 480,
      analysisEndMinute: settings?.analysisEndMinute ?? 1080,
      analysisWeekdays: toWeekdays(settings?.analysisWeekdays),
      pm2ProcessName: settings?.pm2ProcessName || "",
      alertEmailEnabled: settings?.alertEmailEnabled === true,
      alertEmailTo: settings?.alertEmailTo || "",
      smtpHost: settings?.smtpHost || "smtp.hostinger.com",
      smtpPort: settings?.smtpPort ?? 465,
      smtpSecure: settings?.smtpSecure ?? true,
      smtpUser: settings?.smtpUser || "",
      smtpPassword: "",
      smtpFrom: settings?.smtpFrom || "",
      googleContactsEnabled: settings?.googleContactsEnabled === true,
      googleClientId: settings?.googleClientId || "",
      googleClientSecret: "",
      googleContactGroup: settings?.googleContactGroup || "Leads WhatsApp",
      callTranscriptionEnabled: settings?.callTranscriptionEnabled === true,
    });
  }, [settings]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const loadGoogleStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setGoogleStatus(await api.googleStatus());
    } catch {
      setGoogleStatus(null);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    loadGoogleStatus();
  }, [loadGoogleStatus]);

  const handleGoogleConnect = async () => {
    try {
      const { url } = await api.googleAuthUrl();
      // Mesma aba: o retorno do Google precisa do cookie de sessão, e abrir em
      // janela nova costuma esbarrar no bloqueador de pop-up.
      window.location.href = url;
    } catch (error) {
      toast.error(error.message || "Salve o Client ID e o Secret antes de conectar.");
    }
  };

  const handleGoogleSync = async (retryFailed) => {
    setSyncingGoogle(true);
    try {
      const result = await api.googleSync({ retryFailed });
      if (result.skipped) toast(`Sincronização ${result.skipped}.`);
      else toast.success(`${result.synced} contato(s) salvos de ${result.processed}.`);
      await loadGoogleStatus();
    } catch (error) {
      toast.error(error.message || "Não foi possível sincronizar.");
    } finally {
      setSyncingGoogle(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await api.testEmail(formData.alertEmailTo);
      toast.success(`E-mail de teste enviado para ${formData.alertEmailTo}.`);
    } catch (error) {
      toast.error(error.message || "Não foi possível enviar o e-mail.");
    } finally {
      setTestingEmail(false);
    }
  };

  const loadPm2 = async () => {
    try {
      const { processes } = await api.listPm2();
      setPm2List(processes);
      if (processes.length === 0) toast("Nenhum processo retornado pelo PM2.");
    } catch (error) {
      toast.error(error.message || "Não foi possível listar os processos.");
    }
  };

  const handleRestartPm2 = async () => {
    const target = formData.pm2ProcessName;
    const confirmed = window.confirm(
      `Reiniciar o processo "${target}" no servidor?\n\n`
      + "O sistema fica fora do ar por alguns segundos. "
      + "Confirme que este é o processo certo — o servidor hospeda outros sistemas.",
    );

    if (!confirmed) return;

    setRestarting(true);
    try {
      const result = await api.restartPm2(target);
      toast.success(result.message || "Reinício solicitado.");
    } catch (error) {
      toast.error(error.message || "Não foi possível reiniciar.");
    } finally {
      setRestarting(false);
    }
  };

  // As duas janelas guardam os mesmos quatro campos com nomes diferentes; o mapa
  // traduz o campo genérico do componente para a chave real do formulário.
  const updateWindow = (keys) => (field, value) =>
    setFormData((prev) => ({ ...prev, [keys[field]]: value }));

  const handleSave = async () => {
    if (formData.summarizeUrl && !formData.summarizeToken) {
      toast.error(
        "Por favor, forneça o token de acesso para o resumo de conversas.",
      );
      return;
    }

    if (formData.summarizeToken && !formData.summarizeUrl) {
      toast.error(
        "Por favor, forneça a URL da API para o resumo de conversas.",
      );
      return;
    }

    try {
      setSaving(true);
      await updateSettings({
        ...formData,
        slaWeekdays: formData.slaWeekdays.join(","),
        analysisWeekdays: formData.analysisWeekdays.join(","),
      });
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast.error(error.message || "Erro ao salvar configurações.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50">
        <p className="text-gray-600">Carregando configurações...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 p-6">
        <div className="rounded-lg border border-red-200 bg-white p-6 text-center shadow-sm">
          <p className="mb-4 text-red-700">
            Não foi possível carregar as configurações: {error}
          </p>
          <button
            type="button"
            onClick={fetchSettings}
            className="rounded-lg bg-green-500 px-5 py-2 text-white hover:bg-green-600"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">Configurações</h1>

        <div className="space-y-6">
          {/* Configurações Gerais */}
          <div className="rounded-lg border bg-white shadow-sm">
            <div className="border-b p-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Configurações Gerais
              </h2>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Nome da Empresa
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:opacity-70"
                  disabled
                  value={settings?.business || ""}
                  readOnly
                />
              </div>

              {isAdmin && (
                <ScheduleWindow
                  title="Prazo de resposta ao cliente"
                  description="Passado esse tempo sem resposta, o cliente aparece em Aguardando Resposta e no contador do menu."
                  toggleLabel="Contar só no horário de atendimento"
                  toggleHint="Sem isso, mensagem da madrugada acorda atrasada"
                  enabled={formData.slaBusinessHoursEnabled}
                  startMinute={formData.slaStartMinute}
                  endMinute={formData.slaEndMinute}
                  weekdays={formData.slaWeekdays}
                  onChange={updateWindow({
                    enabled: "slaBusinessHoursEnabled",
                    startMinute: "slaStartMinute",
                    endMinute: "slaEndMinute",
                    weekdays: "slaWeekdays",
                  })}
                >
                  <label className="mt-4 block max-w-xs">
                    <span className="mb-1 block text-sm font-medium text-gray-700">
                      Prazo (minutos)
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="10080"
                      value={formData.responseSlaMinutes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          responseSlaMinutes: Number(e.target.value),
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </label>
                </ScheduleWindow>
              )}

              {isAdmin && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Google Contatos
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Salva na agenda do Google o telefone de quem entra em
                        contato, usando o nome do contato. Confere antes se já
                        existe, e marca os salvos para não consultar de novo.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          googleContactsEnabled: !prev.googleContactsEnabled,
                        }))
                      }
                      className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        formData.googleContactsEnabled ? "bg-green-500" : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={formData.googleContactsEnabled}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.googleContactsEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Client ID
                      </span>
                      <input
                        type="text"
                        value={formData.googleClientId}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, googleClientId: e.target.value }))
                        }
                        placeholder="000000-xxxx.apps.googleusercontent.com"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Client Secret
                      </span>
                      <input
                        type="password"
                        value={formData.googleClientSecret}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, googleClientSecret: e.target.value }))
                        }
                        placeholder={
                          settings?.googleClientSecretSet
                            ? "Salvo — deixe em branco para manter"
                            : "GOCSPX-..."
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                    </label>

                    <label className="block md:col-span-2">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Rótulo na agenda do Google
                      </span>
                      <input
                        type="text"
                        value={formData.googleContactGroup}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, googleContactGroup: e.target.value }))
                        }
                        placeholder="Leads WhatsApp"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                      <span className="mt-1 block text-xs text-gray-500">
                        Os contatos entram nesse grupo, separados da agenda
                        pessoal — e dá para removê-los todos de uma vez se
                        precisar.
                      </span>
                    </label>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleGoogleConnect}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      {settings?.googleConnected ? "Reconectar conta" : "Conectar conta Google"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleGoogleSync(false)}
                      disabled={syncingGoogle}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      {syncingGoogle ? "Sincronizando..." : "Sincronizar agora"}
                    </button>

                    {googleStatus?.failed > 0 && (
                      <button
                        type="button"
                        onClick={() => handleGoogleSync(true)}
                        disabled={syncingGoogle}
                        className="rounded-lg border border-amber-300 px-4 py-2 text-sm text-amber-800 hover:bg-amber-50 disabled:opacity-60"
                      >
                        Tentar novamente os que falharam
                      </button>
                    )}
                  </div>

                  {googleStatus && (
                    <p className="mt-2 text-xs text-gray-600">
                      {googleStatus.connected ? "Conta conectada" : "Conta não conectada"} ·{" "}
                      {googleStatus.synced} salvos · {googleStatus.pending} pendentes
                      {googleStatus.failed > 0 && ` · ${googleStatus.failed} com falha`}
                    </p>
                  )}

                  <div className="mt-2 text-xs text-gray-500">
                    No Google Cloud Console, habilite a <strong>People API</strong> e
                    cadastre exatamente esta URI de redirecionamento:
                    <div className="mt-1 flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded bg-gray-100 px-2 py-1">
                        {googleStatus?.redirectUri || "carregando..."}
                      </code>
                      {googleStatus?.redirectUri && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(googleStatus.redirectUri);
                            toast.success("Copiado.");
                          }}
                          className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                        >
                          copiar
                        </button>
                      )}
                    </div>
                    <span className="mt-1 block">
                      É o valor que o servidor envia. Se não for igual ao cadastrado,
                      o Google responde <code>redirect_uri_mismatch</code>.
                    </span>
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Alerta por e-mail
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Avisa quando o WhatsApp sai do ar por mais de 15 minutos
                        e quando volta. É o que evita descobrir a queda dias
                        depois.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          alertEmailEnabled: !prev.alertEmailEnabled,
                        }))
                      }
                      className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        formData.alertEmailEnabled ? "bg-green-500" : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={formData.alertEmailEnabled}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.alertEmailEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    {[
                      ["alertEmailTo", "Enviar alertas para", "voce@escritorio.com.br", "text"],
                      ["smtpHost", "Servidor SMTP", "smtp.hostinger.com", "text"],
                      ["smtpUser", "Usuário (e-mail completo)", "alertas@seudominio.com.br", "text"],
                      ["smtpFrom", "Remetente (opcional)", "CRM <alertas@seudominio.com.br>", "text"],
                    ].map(([key, label, placeholder, type]) => (
                      <label key={key} className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">
                          {label}
                        </span>
                        <input
                          type={type}
                          value={formData[key]}
                          placeholder={placeholder}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                        />
                      </label>
                    ))}

                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Senha
                      </span>
                      <input
                        type="password"
                        value={formData.smtpPassword}
                        placeholder={
                          settings?.smtpPasswordSet
                            ? "Salva — deixe em branco para manter"
                            : "Senha da caixa de e-mail"
                        }
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, smtpPassword: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Porta
                      </span>
                      <select
                        value={formData.smtpPort}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            smtpPort: Number(e.target.value),
                            smtpSecure: Number(e.target.value) === 465,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      >
                        <option value={465}>465 — SSL (padrão Hostinger)</option>
                        <option value={587}>587 — STARTTLS</option>
                      </select>
                    </label>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleTestEmail}
                      disabled={testingEmail}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      {testingEmail ? "Enviando..." : "Enviar e-mail de teste"}
                    </button>
                    <span className="text-xs text-gray-500">
                      Salve antes de testar — o teste usa o que está gravado.
                    </span>
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <h3 className="font-medium text-gray-900">
                    Reinício do servidor (PM2)
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Informe o processo do PM2 deste sistema — o número (ex.:{" "}
                    <code>1</code>) ou o nome (ex.: <code>Escritorio</code>).
                    Use o botão quando o WhatsApp travar e nem o watchdog
                    resolver.
                  </p>

                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <label className="block flex-1 min-w-[12rem]">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Processo
                      </span>
                      <input
                        type="text"
                        value={formData.pm2ProcessName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            pm2ProcessName: e.target.value,
                          }))
                        }
                        placeholder="1 ou Escritorio"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                    </label>

                    <button
                      type="button"
                      onClick={loadPm2}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Listar processos
                    </button>

                    <button
                      type="button"
                      onClick={handleRestartPm2}
                      disabled={restarting || !formData.pm2ProcessName}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {restarting ? "Reiniciando..." : "Reiniciar agora"}
                    </button>
                  </div>

                  {pm2List.length > 0 && (
                    <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            {["id", "Nome", "Status", "Reinícios", ""].map((header) => (
                              <th key={header} className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {pm2List.map((item) => (
                            <tr key={item.id}>
                              <td className="px-3 py-2 text-gray-700">{item.id}</td>
                              <td className="px-3 py-2 text-gray-900">{item.name}</td>
                              <td className="px-3 py-2 text-gray-600">{item.status}</td>
                              <td className="px-3 py-2 text-gray-600">{item.restarts}</td>
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      pm2ProcessName: String(item.name),
                                    }))
                                  }
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  usar este
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <p className="mt-3 text-xs text-gray-500">
                    O servidor hospeda outros sistemas. Confira o nome antes de
                    reiniciar — e prefira restringir os alvos permitidos pela
                    variável <code>PM2_ALLOWED_PROCESSES</code> no servidor.
                  </p>
                </div>
              )}

              {isAdmin && (
                <ScheduleWindow
                  title="Horário de uso da IA"
                  description="Fora dessa faixa a rotina automática não chama a IA — as conversas ficam na fila e entram assim que a janela abrir. Use quando o servidor do modelo não fica ligado o tempo todo. A análise manual continua disponível."
                  toggleLabel="Limitar o horário das análises automáticas"
                  toggleHint="Desligado, a rotina roda a qualquer hora"
                  enabled={formData.analysisWindowEnabled}
                  startMinute={formData.analysisStartMinute}
                  endMinute={formData.analysisEndMinute}
                  weekdays={formData.analysisWeekdays}
                  onChange={updateWindow({
                    enabled: "analysisWindowEnabled",
                    startMinute: "analysisStartMinute",
                    endMinute: "analysisEndMinute",
                    weekdays: "analysisWeekdays",
                  })}
                />
              )}

              {isAdmin && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Assinar mensagens com o nome do atendente
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Quando ativo, toda mensagem enviada pelo CRM chega ao
                        cliente com <strong>*Nome do atendente*:</strong> antes do
                        texto. Desative para o cliente ver apenas a mensagem.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          signMessagesWithAgent: !formData.signMessagesWithAgent,
                        })
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        formData.signMessagesWithAgent
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={formData.signMessagesWithAgent}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.signMessagesWithAgent
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <p className="mt-3 text-xs text-gray-500">
                    Não muda o histórico: mensagens já enviadas continuam como
                    foram. O registro de quem enviou continua guardado no CRM e
                    segue alimentando o desempenho por atendente.
                  </p>
                </div>
              )}

              {isAdmin && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Transcrever e resumir ligações
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Toda ligação atendida pelo CRM é transcrita desde o início,
                        sem clique. O painel da ligação mostra
                        <strong> Transcrevendo · avise o cliente</strong>: o
                        atendente deve avisar no começo da conversa. Ao encerrar, o
                        resumo (feito pela IA de análise configurada abaixo) entra
                        nas notas da conversa.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          callTranscriptionEnabled: !formData.callTranscriptionEnabled,
                        })
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        formData.callTranscriptionEnabled
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={formData.callTranscriptionEnabled}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.callTranscriptionEnabled
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <p className="mt-3 text-xs text-gray-500">
                    LGPD: o áudio não é guardado (é apagado logo após virar texto
                    pelo serviço de transcrição da Groq). A transcrição completa
                    fica guardada no registro da ligação (botão "Ver transcrição"
                    na conversa) e o resumo nas notas. Defina com o jurídico do
                    escritório o texto do aviso ao cliente e por quanto tempo as
                    notas ficam guardadas.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Integrações */}
          <div className="rounded-lg border bg-white shadow-sm">
            <div className="border-b p-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Integrações
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-lg border border-gray-200 p-4">
                  <h3 className="mb-3 font-medium text-gray-900">
                    Análise inteligente de conversas
                  </h3>
                  <p className="mb-4 text-sm text-gray-600">
                    A IA avalia o rumo da conversa, possíveis insatisfações e a
                    conduta do cliente e da equipe de atendimento.
                  </p>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <input
                      value={formData.summarizeUrl}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          summarizeUrl: e.target.value,
                        })
                      }
                      type="url"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      placeholder="URL da API"
                    />

                    <input
                      value={formData.summarizeToken}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          summarizeToken: e.target.value,
                        })
                      }
                      type="password"
                      autoComplete="off"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      placeholder="Token de Acesso"
                    />
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Minutos sem novas mensagens
                      <input
                        type="number"
                        min="1"
                        max="10080"
                        value={formData.analysisInactivityMinutes}
                        onChange={(e) => setFormData({
                          ...formData,
                          analysisInactivityMinutes: Number(e.target.value),
                        })}
                        className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                    </label>
                    <label className="block text-sm font-medium text-gray-700">
                      Mínimo de mensagens
                      <input
                        type="number"
                        min="2"
                        max="1000"
                        value={formData.analysisMinMessages}
                        onChange={(e) => setFormData({
                          ...formData,
                          analysisMinMessages: Number(e.target.value),
                        })}
                        className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      />
                    </label>
                  </div>
                  <label className="mt-5 flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-4">
                    <span>
                      <span className="block font-medium text-gray-900">Análise automática</span>
                      <span className="block text-sm text-gray-600">
                        Processa conversas inativas que receberam mensagens novas.
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      checked={formData.analysisEnabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        analysisEnabled: e.target.checked,
                      })}
                      className="h-5 w-5 rounded border-gray-300 text-green-600"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={resetForm}
              disabled={saving}
              className="rounded-lg border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !isAdmin}
              title={
                isAdmin ? undefined : "Apenas administradores alteram as configurações"
              }
              className="rounded-lg bg-green-500 px-6 py-2 text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Salvar Configurações"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
